import React, { useMemo, useState } from 'react';
import {
  Button,
  DataCell,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  ListItem,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import { DataList } from '@/components/ui/DataList';
import {
  IconCheck,
  IconChevronDown,
  IconClock,
  IconLoader,
  IconX,
} from '@tabler/icons-react';
import StatusCell from './StatusCell';
import { useCsvApproval } from '@/hooks/useCsvApproval';
import {
  CsvFile,
  CsvFileVersion,
  CsvFileValidationStatus,
} from '@/types/csvFiles';
import { getValidationStatusInfo } from '@/utils/csvFileUtils';

interface ValidationStatusCellProps {
  value?: unknown;
  file?: CsvFile | CsvFileVersion; // Can work with both CsvFile and CsvFileVersion
  onStatusChange?: (file: CsvFile | CsvFileVersion, status: CsvFileValidationStatus) => void;
  render?: () => React.ReactNode;
}

function ValidationStatusCell({
  value,
  file,
  onStatusChange,
  render,
}: ValidationStatusCellProps) {
  const {
    canApprove,
    approveFile,
    rejectFile,
    updateValidationStatus,
    approveVersion,
    rejectVersion,
    updateVersionStatus,
  } = useCsvApproval();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<'approve' | 'reject' | null>(null);

  // Derived values must be computed unconditionally (React hooks rules).
  const status = useMemo<CsvFileValidationStatus>(() => {
    const statusFromFile =
      file && 'validation_status' in file ? file.validation_status : undefined;
    return (statusFromFile || value || 'pending') as CsvFileValidationStatus;
  }, [file, value]);

  const statusInfo = useMemo(() => {
    return file ? getValidationStatusInfo(file) : null;
  }, [file]);

  // Render StatusCell with hover card if status info is available
  const renderStatusCellWithHover = () => {
    const statusCell = (
      <StatusCell
        value={status}
        entityType="csv_file"
        status={status}
        size="small"
        className={statusInfo ? 'badge--hoverable' : undefined}
      />
    );

    // If no status info, return status cell without hover
    if (!statusInfo) {
      return statusCell;
    }

    // Wrap StatusCell in HoverCard with status information
    return (
      <HoverCardRoot>
        <HoverCardTrigger>
          <div style={{ display: 'inline-block' }}>{statusCell}</div>
        </HoverCardTrigger>
        <HoverCardContent size="2">
          <Flex direction="column" gap="3">
            <Text size="xsmall" weight="medium">
              Approval Information
            </Text>
            <DataList.Root
              style={{
                gap: 'var(--space-4) var(--space-2)',
              }}
            >
              <DataList.Item>
                <DataList.Label>Status</DataList.Label>
                <DataList.Value>
                  <StatusCell
                    value={status}
                    entityType="csv_file"
                    status={status}
                    size="small"
                  />
                </DataList.Value>
              </DataList.Item>
              {statusInfo.changedAt && (
                <DataList.Item>
                  <DataList.Label>
                    {status === 'approved' ? 'Approved' : 'Rejected'}
                  </DataList.Label>
                  <DataList.Value>
                    <DataCell type="datetime" value={statusInfo.changedAt} />
                  </DataList.Value>
                </DataList.Item>
              )}
              {statusInfo.changedBy && (
                <DataList.Item>
                  <DataList.Label>
                    {status === 'approved' ? 'Approved By' : 'Rejected By'}
                  </DataList.Label>
                  <DataList.Value>
                    <DataCell type="user" value={statusInfo.changedBy} />
                  </DataList.Value>
                </DataList.Item>
              )}
            </DataList.Root>
          </Flex>
        </HoverCardContent>
      </HoverCardRoot>
    );
  };

  // Check if this is a version (has csv_file_id)
  const isVersion = useMemo(() => {
    return file && 'csv_file_id' in file;
  }, [file]);

  // Imported versions/files are read-only (status can no longer be changed)
  const isImported = useMemo(() => {
    if (!file) return false;

    // Versions: imported_at is present when imported
    if ('csv_file_id' in file) {
      return Boolean((file as CsvFileVersion).imported_at);
    }

    // Files: treat as imported if the latest/last version is imported
    const csvFile = file as CsvFile;
    return Boolean(csvFile.last_version?.imported_at);
  }, [file]);

  if (render) {
    return <>{render()}</>;
  }

  if (!file) {
    return null;
  }

  const handleApprove = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setProcessingAction('approve');
    try {
      if (isVersion) {
        const version = file as CsvFileVersion;
        // Mutation hook automatically invalidates cache and shows toast
        await approveVersion(version.csv_file_id!, version);
      } else {
        await approveFile(file as CsvFile);
      }
      // Notify parent component of status change
      onStatusChange?.(file!, 'approved');
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const handleReject = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setProcessingAction('reject');
    try {
      if (isVersion) {
        const version = file as CsvFileVersion;
        // Mutation hook automatically invalidates cache and shows toast
        await rejectVersion(version.csv_file_id!, version);
      } else {
        await rejectFile(file as CsvFile);
      }
      // Notify parent component of status change
      onStatusChange?.(file!, 'rejected');
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const handleStatusChange = async (newStatus: CsvFileValidationStatus) => {
    if (isProcessing || newStatus === status) return;
    setIsProcessing(true);
    try {
      if (isVersion) {
        const version = file as CsvFileVersion;
        // Mutation hook automatically invalidates cache and shows toast
        await updateVersionStatus(version.csv_file_id!, version, newStatus);
      } else {
        await updateValidationStatus(file as CsvFile, newStatus);
      }
      // Notify parent component of status change
      onStatusChange?.(file!, newStatus);
    } finally {
      setIsProcessing(false);
    }
  };

  // For versions without approval permissions, show status with hover card only (read-only)
  if (isVersion && !canApprove) {
    return renderStatusCellWithHover();
  }

  // Imported entities are always read-only (even for approvers)
  if (isImported) {
    return renderStatusCellWithHover();
  }

  // For users without approval permissions, show status with hover card if available
  // This allows them to see who approved/rejected their files
  if (!canApprove) {
    return renderStatusCellWithHover();
  }

  // For users with approval permissions
  if (status === 'pending') {
    // Show Approve/Reject buttons for pending status
    return (
      <Flex gap="1" align="center">
        <VisuallyHidden>
          <StatusCell
            value={status}
            entityType="csv_file"
            status={status}
            size="small"
          />
        </VisuallyHidden>
        <Button
          variant="outlined"
          size="xsmall"
          color="success"
          startIcon={
            processingAction === 'approve' ? (
              <IconLoader className="animate-spin" />
            ) : (
              <IconCheck />
            )
          }
          onClick={handleApprove}
          disabled={isProcessing}
          title="Approve file"
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          size="xsmall"
          color="error"
          startIcon={
            processingAction === 'reject' ? (
              <IconLoader className="animate-spin" />
            ) : (
              <IconX />
            )
          }
          onClick={handleReject}
          disabled={isProcessing}
          title="Reject file"
        >
          Reject
        </Button>
      </Flex>
    );
  }

  // For approved/rejected status, show dropdown to change status
  return (
    <Flex gap="1" align="center">
      {renderStatusCellWithHover()}
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="outlined"
            size="xsmall"
            endIcon={<IconChevronDown />}
            isIconOnly
            disabled={isProcessing}
            title="Change status"
          >
            Change
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent variant="soft" color="gray">
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={<IconClock />}
              primary="Set to Pending"
              onClick={() => handleStatusChange('pending')}
              disabled={isProcessing}
            />
          </DropdownMenuItem>
          {status !== 'approved' && (
            <DropdownMenuItem asChild>
              <ListItem
                primary="Approve"
                startIcon={<IconCheck />}
                onClick={() => handleStatusChange('approved')}
                disabled={isProcessing}
              />
            </DropdownMenuItem>
          )}
          {status !== 'rejected' && (
            <DropdownMenuItem asChild>
              <ListItem
                primary="Reject"
                startIcon={<IconX />}
                onClick={() => handleStatusChange('rejected')}
                disabled={isProcessing}
              />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </Flex>
  );
}

export default ValidationStatusCell;

export function ValidationStatusCellSkeleton() {
  return (
    <div className="data-cell__validation-status-skeleton">
      <div className="skeleton skeleton--badge" style={{ width: '80px' }} />
    </div>
  );
}

